import { PropsWithChildren, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'
import { BaseModal } from 'components/organisms/modals/base-modal'

interface Props {
	open: boolean
	setOpen?: (...args: any) => any
	onSave?: (...args: any) => any
}

const validationSchema = Yup.object().shape({
	slice: Yup.number().typeError('You must specify a number').required('Number of slices is required'),
	price: Yup.number().typeError('You must specify a number').required('Pie price is required'),
})

export function SliceSelectCustomAmountModal({ open, setOpen, onSave }: PropsWithChildren<Props>) {
	const {
		control,
		register,
		handleSubmit,
		formState: { errors, isValid, isDirty },
	} = useForm({
		resolver: yupResolver(validationSchema),
		mode: 'onBlur', // "onChange"
	})

	const onSubmit = (values: any) => {
		onSave?.(values)
		setOpen?.(false)
	}

	return (
		<BaseModal
			open={open}
			setOpen={setOpen}
			title="Custom amount"
			size="sm"
			customSize="sm:max-w-[294px]"
			className="px-6 py-8 pb-10"
			hideCloseButton
		>
			<div className="text-left">
				<form onSubmit={handleSubmit(onSubmit)}>
					<div className="space-y-4">
						<div>
							<label htmlFor="slice" className="text-primary-text font-medium block text-xs uppercase">
								Number of slices
							</label>
							<div className="mt-2">
								<Controller
									control={control}
									name="slice"
									render={({ field }) => (
										<input
											id="url"
											type="text"
											className="px-4 py-3 border-2 border-translucent-plus-border bg-primary-surface/[0.4] backdrop-blur-lg rounded-lg text-primary-text placeholder-secondary-text placeholder:italic placeholder:font-light block w-full h-12"
											placeholder="Add number"
											{...field}
											value={field.value ?? ''}
										/>
									)}
								/>
								{errors.slice && (
									<div className="text-secondary-red mt-2 text-sm">
										{errors.slice.message as string}
									</div>
								)}
							</div>
						</div>
						<div>
							<label htmlFor="price" className="text-primary-text font-medium block text-xs uppercase">
								Pie price
							</label>
							<div className="mt-2">
								<label className="px-4 py-3 border-2 border-translucent-plus-border bg-primary-surface/[0.4] backdrop-blur-lg rounded-lg text-primary-text placeholder-secondary-text placeholder:italic placeholder:font-light w-full h-12 flex items-center">
									<span className="mr-1">$</span>
									<input
										id="price"
										type="text"
										className="bg-transparent focus:ring-0 p-0 border-none flex-1 placeholder-white/50 placeholder:italic placeholder:font-light"
										placeholder="0"
										{...register('price')}
									/>
								</label>
								{errors.price && (
									<div className="text-secondary-red mt-2 text-sm">
										{errors.price.message as string}
									</div>
								)}
							</div>
						</div>
					</div>
				</form>
			</div>
			<div className="mt-10 space-y-2">
				<button
					onClick={handleSubmit(onSubmit)}
					className="w-full h-10 rounded-full flex justify-center items-center transition-all duration-300 bg-primary text-white font-bold hover:bg-primary-hover disabled:bg-white-25 disabled:text-white-25 disabled:backdrop-blur-lg"
					disabled={!(isValid && isDirty)}
				>
					Save
				</button>
				<button
					onClick={() => setOpen?.(false)}
					className="w-full h-10 rounded-full flex justify-center items-center transition-all duration-300 bg-white/10 text-white font-bold shadow-button backdrop-blur-lg hover:bg-white-25 disabled:bg-white/10 disabled:text-white-25 disabled:shadow-none"
				>
					Cancel
				</button>
			</div>
		</BaseModal>
	)
}

// ---------------------------------------------------------------------------
// Modal Manager integration
// ---------------------------------------------------------------------------

export interface SliceSelectCustomAmountModalManagedProps {
	onSave?: (...args: any) => any
}

declare module 'components/organisms/modals/modal-registry' {
	interface ModalRegistry {
		'slice-select-custom-amount': SliceSelectCustomAmountModalManagedProps
	}
}

export function ManagedSliceSelectCustomAmountModal({
	onClose,
	...rest
}: SliceSelectCustomAmountModalManagedProps & { onClose: () => void }) {
	const [open, setOpen] = useState(true)

	const handleClose = () => {
		setOpen(false)
		setTimeout(onClose, 200)
	}

	return (
		<SliceSelectCustomAmountModal
			open={open}
			setOpen={(v: boolean) => { if (!v) handleClose() }}
			{...rest}
		/>
	)
}
